import { Button, Checkbox, Input, InputGroup } from "@heroui/react";
import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useSignIn } from "@clerk/react";
import { AlertCircle, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { cn } from "@/utils/cn";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M17.64 9.20454C17.64 8.56636 17.5827 7.95272 17.4764 7.36363H9V10.845H13.8436C13.635 11.97 13.0009 12.9231 12.0477 13.5613V15.8195H14.9564C16.6582 14.2527 17.64 11.9454 17.64 9.20454Z"
      fill="#4285F4"
    />
    <path
      d="M9 18C11.43 18 13.4673 17.1941 14.9564 15.8195L12.0477 13.5613C11.2418 14.1013 10.2109 14.4204 9 14.4204C6.65591 14.4204 4.67182 12.8372 3.96409 10.71H0.957275V13.0418C2.43818 15.9831 5.48182 18 9 18Z"
      fill="#34A853"
    />
    <path
      d="M3.96409 10.71C3.78409 10.17 3.68182 9.59318 3.68182 9C3.68182 8.40682 3.78409 7.83 3.96409 7.29V4.95818H0.957275C0.347727 6.17318 0 7.54772 0 9C0 10.4523 0.347727 11.8268 0.957275 13.0418L3.96409 10.71Z"
      fill="#FBBC05"
    />
    <path
      d="M9 3.57955C10.3214 3.57955 11.5077 4.03364 12.4405 4.92545L15.0218 2.34409C13.4632 0.891818 11.4259 0 9 0C5.48182 0 2.43818 2.01682 0.957275 4.95818L3.96409 7.29C4.67182 5.16273 6.65591 3.57955 9 3.57955Z"
      fill="#EA4335"
    />
  </svg>
);

const GENERIC_ERROR = "Something went wrong. Please try again.";

const readError = (err, fallback = GENERIC_ERROR) =>
  err?.longMessage ||
  err?.message ||
  err?.errors?.[0]?.longMessage ||
  err?.errors?.[0]?.message ||
  fallback;

const COPY = {
  "sign-in": {
    heading: (
      <>
        Log into <br /> your account
      </>
    ),
    caption: null,
  },
  "reset-email": {
    heading: (
      <>
        Reset your <br /> password
      </>
    ),
    caption: "Enter your email and we'll send you a reset code.",
  },
  "reset-code": {
    heading: (
      <>
        Check your <br /> inbox
      </>
    ),
    caption: "Enter the 6-digit code we sent you.",
  },
  "reset-password": {
    heading: (
      <>
        Choose a new <br /> password
      </>
    ),
    caption: "You'll be logged in once your password is saved.",
  },
};

const SignInPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useSignIn();

  // "sign-in" | "reset-email" | "reset-code" | "reset-password"
  const [step, setStep] = useState("sign-in");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const busy = loading || googleLoading;

  const finalizeSignIn = async () => {
    await signIn.finalize({
      navigate: ({ session, decorateUrl }) => {
        if (session?.currentTask) {
          console.log(session.currentTask);
          return;
        }
        const from = location.state?.from || "/";
        const url = decorateUrl(from);
        if (url.startsWith("http")) {
          window.location.href = url;
        } else {
          navigate(url, { replace: true });
        }
      },
    });
  };

  const goToStep = (next) => {
    setError(null);
    setNotice(null);
    setStep(next);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { error: signInError } = await signIn.password({
        identifier: email,
        password,
      });

      if (signInError) {
        if (signInError.code === "form_password_pwned") {
          setError(
            "This password has appeared in a data breach. Reset it to continue."
          );
          goToStep("reset-email");
          return;
        }
        setError(readError(signInError));
        return;
      }

      if (signIn.status === "complete") {
        await finalizeSignIn();
      } else if (signIn.status === "needs_second_factor") {
        navigate("/sign-in/mfa", { replace: true });
      } else {
        setError("Sign in could not be completed. Please try again.");
      }
    } catch (err) {
      setError(readError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSendResetCode = async (e) => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setLoading(true);

    try {
      const { error: createError } = await signIn.create({ identifier: email });
      if (createError) {
        setError(readError(createError, "We couldn't find an account for that email."));
        return;
      }

      const { error: sendCodeError } = await signIn.resetPasswordEmailCode.sendCode();
      if (sendCodeError) {
        setError(readError(sendCodeError, "We couldn't send the reset code. Please try again."));
        return;
      }

      setCode("");
      setStep("reset-code");
    } catch (err) {
      setError(readError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setLoading(true);

    try {
      const { error: verifyError } = await signIn.resetPasswordEmailCode.verifyCode({
        code,
      });

      if (verifyError) {
        setError(readError(verifyError, "That code isn't valid. Check it and try again."));
        return;
      }

      setStep("reset-password");
    } catch (err) {
      setError(readError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitNewPassword = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const { error: submitError } = await signIn.resetPasswordEmailCode.submitPassword({
        password: newPassword,
        signOutOfOtherSessions: true,
      });

      if (submitError) {
        setError(readError(submitError, "We couldn't save that password."));
        return;
      }

      if (signIn.status === "complete") {
        await finalizeSignIn();
      } else if (signIn.status === "needs_second_factor") {
        navigate("/sign-in/mfa", { replace: true });
      } else {
        setError("Your password was saved, but sign in didn't complete. Please log in.");
        goToStep("sign-in");
      }
    } catch (err) {
      setError(readError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError(null);
    setNotice(null);
    setLoading(true);

    try {
      const { error: sendCodeError } = await signIn.resetPasswordEmailCode.sendCode();
      if (sendCodeError) {
        setError(readError(sendCodeError, "We couldn't resend the code. Please try again."));
        return;
      }
      setNotice("New code sent.");
    } catch (err) {
      setError(readError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setGoogleLoading(true);

    try {
      const from = location.state?.from || "/";
      sessionStorage.setItem("oauth_redirect", from);

      const { error: ssoError } = await signIn.sso({
        strategy: "oauth_google",
        redirectCallbackUrl: "/sso-callback",
        redirectUrl: "/",
      });

      if (ssoError) {
        setError(readError(ssoError, "Google sign in failed. Please try again."));
        setGoogleLoading(false);
      }

    } catch (err) {
      console.log(err);
      setError(readError(err, "Google sign in failed. Please try again."));
      setGoogleLoading(false);
    }
  };

  const isResetting = step !== "sign-in";
  const { heading, caption } = COPY[step];

  return (
    <div className="auth-page relative flex flex-col lg:items-start justify-center">
      <img src="/bg-light.svg" className="h-screen w-screen absolute top-0" />
      <div className="flex flex-col gap-10 items-start justify-center section-padding-x relative h-screen w-screen backdrop-blur-xl bg-backdrop/30">
        <div className="flex flex-col items-start md:mt-0 mt-28 lg:w-2/5 md:w-3/5 mx-auto gap-4">
          <div className="flex items-center">
            <div className="rounded-full">
              <img src="/logo-text.svg" width={68} alt="" />
            </div>
          </div>

          <div className="flex flex-col text-start! items-start w-full gap-5">
            <span className="font-bold leading-[36px] text-[40px] text-glass-foreground lg:max-w-[unset] max-w-4/5">
              {heading}
            </span>
            {caption && (
              <span className={cn("text-glass-foreground text-sm tracking-[0.2px] max-w-4/5", step === 'reset-email' && 'max-w-[200px]')}>
                {caption}
              </span>
            )}
          </div>
        </div>

        {/* ---------------------------------------------------------------- */}
        {/* Sign in                                                          */}
        {/* ---------------------------------------------------------------- */}
        {step === "sign-in" && (
          <form className="flex flex-col lg:w-2/5 md:w-3/5 mx-auto gap-10" onSubmit={handleSubmit}>
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-6">
                <Input
                  placeholder="Email"
                  className={"auth-input"}
                  fullWidth
                  autoComplete="email"
                  name="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />

                <InputGroup className="auth-input px-0">
                  <InputGroup.Input
                    placeholder="Password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className={"px-0 placeholder:text-glass-foreground"}
                  />
                  <InputGroup.Suffix>
                    <button
                      type="button"
                      onClick={() => setShowPassword((prev) => !prev)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                      className="text-background/70 hover:text-background transition-colors"
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </InputGroup.Suffix>
                </InputGroup>
              </div>

              <div className="flex items-center justify-between">
                <Checkbox name="remember-me" isSelected={rememberMe} onChange={setRememberMe}>
                  <Checkbox.Content className="flex flex-row gap-2 items-center">
                    <Checkbox.Control className="bg-transparent border-glass-foreground border">
                      <Checkbox.Indicator />
                    </Checkbox.Control>
                    <span className="text-glass-foreground text-sm tracking-[0.2px]">
                      Remember Me
                    </span>
                  </Checkbox.Content>
                </Checkbox>

                <button
                  type="button"
                  onClick={() => goToStep("reset-email")}
                  className="text-glass-foreground text-sm tracking-[0.2px] underline-offset-4 hover:underline"
                >
                  Forgot Password
                </button>
              </div>
            </div>

            <div>
              <Button
                type="submit"
                className={"w-full disabled:opacity-100"}
                isPending={loading}
                isDisabled={busy}
              >
                {loading ? 'logging in...' : 'Log In'}
              </Button>

              <div className="flex items-center text-sm text-center justify-center pt-4 gap-1 text-glass-foreground">
                <span>Don't have an account?</span>
                <Link to="/sign-up">
                  <button type="button" className="underline">
                    Sign Up
                  </button>
                </Link>
              </div>
            </div>
          </form>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* Reset step 1 — email                                             */}
        {/* ---------------------------------------------------------------- */}
        {step === "reset-email" && (
          <form className="flex flex-col gap-10" onSubmit={handleSendResetCode}>
            <Input
              placeholder="Email"
              className={"auth-input"}
              fullWidth
              autoComplete="email"
              name="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <div>
              <Button
                type="submit"
                className={"w-full disabled:opacity-100"}
                isPending={loading}
                isDisabled={busy || !email}
              >
                Send reset code
              </Button>
            </div>
          </form>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* Reset step 2 — code                                              */}
        {/* ---------------------------------------------------------------- */}
        {step === "reset-code" && (
          <form className="flex flex-col gap-10" onSubmit={handleVerifyCode}>
            <Input
              placeholder="Reset code"
              className={"auth-input tracking-[0.3em]"}
              fullWidth
              name="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
            />

            <div>
              <Button
                type="submit"
                className={"w-full disabled:opacity-100"}
                isPending={loading}
                isDisabled={busy || !code}
              >
                Verify code
              </Button>

              <div className="flex items-center text-sm text-center justify-center pt-4 gap-1 text-glass-foreground">
                <span>Didn't get it?</span>
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={busy}
                  className="underline disabled:opacity-60"
                >
                  Resend code
                </button>
              </div>
            </div>
          </form>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* Reset step 3 — new password                                      */}
        {/* ---------------------------------------------------------------- */}
        {step === "reset-password" && (
          <form className="flex flex-col gap-10" onSubmit={handleSubmitNewPassword}>
            <InputGroup className="auth-input px-0">
              <InputGroup.Input
                placeholder="New password"
                type={showNewPassword ? "text" : "password"}
                autoComplete="new-password"
                name="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className={"px-0 placeholder:text-glass-foreground"}
              />
              <InputGroup.Suffix>
                <button
                  type="button"
                  onClick={() => setShowNewPassword((prev) => !prev)}
                  aria-label={showNewPassword ? "Hide password" : "Show password"}
                  className="text-background/70 hover:text-background transition-colors"
                >
                  {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </InputGroup.Suffix>
            </InputGroup>

            <div>
              <Button
                type="submit"
                className={"w-full disabled:opacity-100"}
                isPending={loading}
                isDisabled={busy || !newPassword}
              >
                Save password
              </Button>
            </div>
          </form>
        )}

        {isResetting && (
          <button
            type="button"
            onClick={() => goToStep("sign-in")}
            className="flex items-center gap-1.5 text-glass-foreground text-sm tracking-[0.2px] lg:w-2/5 md:w-3/5 mx-auto"
          >
            <ArrowLeft size={16} />
            Back to log in
          </button>
        )}

        {error && (
          <div className="flex items-start gap-2 rounded-xl bg-danger/15 border border-danger/30 px-4 py-3 text-sm text-danger lg:w-2/5 md:w-3/5 mx-auto">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {notice && !error && (
          <div className="rounded-xl bg-glass/20 border border-glass-border px-4 py-3 text-sm text-glass-foreground lg:w-2/5 md:w-3/5 mx-auto">
            {notice}
          </div>
        )}
      </div>
    </div>
  );
};

export default SignInPage;